import { useEffect, useState } from "react";
import { X, Download, Share2, Plus } from "lucide-react";

const DISMISS_KEY = "qrtourist:install-dismissed-at";
const DISMISS_DAYS = 7;
const SHOW_AFTER_MS = 30000; // 30s

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function wasDismissedRecently() {
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  if (!ts) return false;
  return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (wasDismissedRecently()) return;

    // Android / Chrome path
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS path: show only on Safari, after delay
    const ios = isIOS();
    if (ios) setIosMode(true);

    const t = setTimeout(() => {
      if (deferredPrompt || ios) setShow(true);
    }, SHOW_AFTER_MS);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      clearTimeout(t);
    };
  }, [deferredPrompt]);

  // Re-evaluate visibility when deferredPrompt arrives
  useEffect(() => {
    if (!isStandalone() && !wasDismissedRecently() && deferredPrompt) {
      const t = setTimeout(() => setShow(true), 5000);
      return () => clearTimeout(t);
    }
  }, [deferredPrompt]);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  }

  async function install() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {}
    setDeferredPrompt(null);
    setShow(false);
  }

  if (!show) return null;
  // If iOS, always show iOS instructions; if Android with deferredPrompt, show install button
  if (!iosMode && !deferredPrompt) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed left-4 right-4 bottom-4 z-50 max-w-md mx-auto bg-white rounded-2xl shadow-2xl border-2 border-brand/40 overflow-hidden animate-in slide-in-from-bottom-5"
      style={{ animation: "slideUp 0.3s ease-out" }}
    >
      <div className="flex items-start gap-3 p-4">
        <img src="/icons/icon-192.png" alt="" className="w-12 h-12 rounded-xl shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-ink text-[15px] leading-tight">Installa QrTourist</div>
          <div className="text-xs text-ink-soft mt-1 leading-relaxed">
            {iosMode
              ? <>Tocca <Share2 size={12} className="inline -mt-0.5" /> Condividi, poi <strong>"Aggiungi a Home"</strong> <Plus size={12} className="inline" />.</>
              : "Accesso istantaneo, audio offline, niente browser bar."}
          </div>
          {!iosMode && deferredPrompt && (
            <button
              data-testid="install-prompt-btn"
              onClick={install}
              className="mt-3 inline-flex items-center gap-1.5 bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-full text-sm font-semibold transition"
            >
              <Download size={14} /> Installa
            </button>
          )}
        </div>
        <button
          data-testid="install-prompt-close"
          onClick={dismiss}
          aria-label="Chiudi"
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-bg shrink-0 text-ink-soft hover:text-ink transition"
        >
          <X size={16} />
        </button>
      </div>
      <style>{`@keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </div>
  );
}
